/**
 * The Pro page's sections — one source for the first-time stepper and the dashboard's EDIT PAGE
 * tab (design_handoff_pro_setup/README.md "Dynamic steps"). Everything that lists, counts or
 * numbers steps derives from `visibleFor(kind)`: the chips, the progress bar, STEP n OF m, the
 * rail, and the kind card's "your page has these sections" preview. Numbers are positions in
 * the visible list, never the global index.
 */
export const STEPS = ['identity', 'kind', 'list', 'extras', 'schedule', 'where', 'attributes', 'layout', 'top', 'offers', 'photos', 'publish'];

export const SKIPS = {
  // OFFERS is on every kind — a bookable pro posts claimable offers like a place does.
  bookable: ['extras', 'where', 'attributes', 'layout'],
  eatery: [],
  bar: [],
  venue: ['layout', 'top'],
  shop: [],
};

export const KIND_KEYS = ['bookable', 'eatery', 'bar', 'venue', 'shop'];

export const visibleFor = (kind) => STEPS.filter((k) => !(SKIPS[kind] || SKIPS.bookable).includes(k));

const LIST_LABEL = { bookable: 'SERVICES', eatery: 'MENU', bar: 'DRINKS', venue: 'EVENTS', shop: 'SERVICES' };
const EXTRAS_LABEL = { eatery: 'CUISINE & LINKS', bar: 'LINKS & CONTACT', venue: 'THE ROOM', shop: 'BRANDS & LINKS' };

/** The label of a step for a kind. `mode` only changes PUBLISH (stepper) → LINKS & … (EDIT PAGE). */
export function stepLabel(key, kind, mode = 'create') {
  switch (key) {
    case 'identity': return 'IDENTITY';
    case 'kind': return 'KIND';
    case 'list': return LIST_LABEL[kind] || LIST_LABEL.bookable;
    case 'extras': return EXTRAS_LABEL[kind] || 'EXTRAS';
    case 'schedule': return kind === 'bookable' ? 'AVAILABILITY' : kind === 'bar' ? 'HOURS & HAPPY HOUR' : 'HOURS';
    case 'where': return 'WHERE';
    case 'attributes': return 'ATTRIBUTES';
    case 'layout': return 'MENU LAYOUT';
    case 'top': return 'TOP THINGS';
    case 'offers': return 'OFFERS';
    case 'photos': return 'PHOTOS & COVER';
    case 'publish': return mode === 'create' ? 'PUBLISH' : kind === 'bookable' ? 'LINKS & FLAKE POLICY' : 'LINKS & VISIBILITY';
    default: return key.toUpperCase();
  }
}

/** The one-line description under a step's title. */
export function stepDesc(key, kind, mode = 'create') {
  const bk = kind === 'bookable';
  switch (key) {
    case 'identity': return 'Name, what you do, where you’re based and your line.';
    case 'kind': return 'What kind of business this is. It decides which sections your page has.';
    case 'list': return {
      bookable: 'What you do and what it costs. A line with a length can be booked straight from your page.',
      eatery: 'Sections and dishes. Paste a menu or import it from your site to start fast.',
      bar: 'Taps, cocktails, wine and snacks. Turn the ABV column on per section.',
      shop: 'Services with from-prices. Each line is BOOK or QUOTE.',
      venue: 'Events linked to your page. They show on your EVENTS tab straight away.',
    }[kind] || '';
    case 'extras': return {
      eatery: 'Cuisine, price band, reservation and order links.',
      bar: 'What kind of place, your website and phone.',
      venue: 'Capacity, doors, hire rate and the sound spec.',
      shop: 'Brands you carry, certifications, warranty and links.',
    }[kind] || '';
    case 'schedule': return bk
      ? 'The hours you work, the days that break the pattern, and the calendars that block you out.'
      : `When you’re open, day by day.${kind === 'eatery' ? ' Add when the kitchen closes if it’s before the room.' : ''}`;
    case 'where': return 'Street address, neighbourhood and parking.';
    case 'attributes': return 'The facts people filter by. The set depends on your kind.';
    case 'layout': return 'How your menu looks on the page, overall and per section.';
    case 'top': return 'Up to six lines. They become WHAT THEY’RE KNOWN FOR on your page.';
    case 'offers': return bk ? 'Things a guest can claim and bring to you. You scan them when they turn up.' : 'Things a guest can claim and bring in. You scan them at the counter.';
    case 'photos': return 'Your gallery, the cover, your logo and the faces. The preview shows what guests see.';
    case 'publish': return `Links, ${bk ? 'your flake policy, ' : ''}${mode === 'create' ? 'who manages the page, and going live.' : 'and whether the page is live. Who manages it lives in SETTINGS → ROLES & ACCESS.'}`;
    default: return '';
  }
}

/** STEP n OF m for a key within a kind's visible list (1-based); null when the step is hidden. */
export function stepNumber(key, kind) {
  const i = visibleFor(kind).indexOf(key);
  return i < 0 ? null : { n: i + 1, of: visibleFor(kind).length };
}

/** The primary CTA on a phone preview, by kind. */
export const PRIMARY_CTA = { bookable: 'REQUEST A BOOKING', eatery: 'SEE THE MENU', bar: 'SEE THE DRINKS', venue: 'WHAT’S ON', shop: 'SEE SERVICES' };

/** The dashboard SET UP rows: [label, description, section]. */
export function setupRows(kind) {
  const bk = kind === 'bookable';
  return [
    ['KIND', 'Change what kind of business this is. Your page’s sections follow it.', 'kind'],
    [bk ? 'AVAILABILITY' : 'HOURS', bk ? 'Hours you work, specific dates and connected calendars.' : 'Opening hours, day by day.', 'schedule'],
    ...(bk ? [] : [['WHERE', 'Street address, neighbourhood and parking.', 'where']]),
    ['PHOTOS & COVER', 'Gallery, cover layout, logo and faces — with a live preview.', 'photos'],
    [bk ? 'LINKS & FLAKE POLICY' : 'LINKS & VISIBILITY', `${bk ? 'Links on your page, cancellation tiers' : 'Links on your page'}, and whether it’s live. Roles are below.`, 'publish'],
  ];
}
